import React, { createContext, useState, useContext, useEffect, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { clearCache } from '../services/api';

interface BranchContextType {
  selectedBranch: string;
  setSelectedBranch: (branch: string) => void;
  availableBranches: string[];
}

export const BranchContext = createContext<BranchContextType | undefined>(undefined);

export const BranchProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [selectedBranch, setSelectedBranchState] = useState<string>(() => {
    return localStorage.getItem('selectedBranch') || 'Tüm Şubeler';
  });

  const availableBranches = React.useMemo(() => {
    // If the user is a coordinator, only return their explicitly assigned branches
    if (user?.role === 'coordinator' && Array.isArray(user?.branchCodes)) {
      console.debug('[BranchContext] Coordinator available branches:', user.branchCodes);
      return user.branchCodes;
    }

    // Robust check for both casings
    const org = user?.Organization || (user as any)?.organization;
    
    if (!org || !org.branches) {
      console.debug('[BranchContext] No branches found. Org exists:', !!org);
      return [];
    }
    
    // Ensure branches is an array before mapping
    const branches = Array.isArray(org.branches) ? org.branches : [];
    const map = branches.map((b: any) => b.city).filter(Boolean);
    
    console.debug('[BranchContext] Available branches:', map);
    return map;
  }, [user]);

  const setSelectedBranch = (branch: string) => {
    setSelectedBranchState(branch);
    localStorage.setItem('selectedBranch', branch);
    clearCache('workplaces');
    clearCache('statistics');
    clearCache('dashboard_summary');
    clearCache('general_statistics');
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('branchChanged', { detail: { branch } }));
      window.dispatchEvent(new Event('workplaceUpdated'));
    }
  };

  // If user is a coordinator, they should not be allowed to select "Tüm Şubeler".
  // Force selection to their first assigned branch.
  useEffect(() => {
    if (user?.role === 'coordinator' && availableBranches.length > 0) {
      if (selectedBranch === 'Tüm Şubeler' || !availableBranches.includes(selectedBranch)) {
        setSelectedBranch(availableBranches[0]);
      }
    }
  }, [user, availableBranches, selectedBranch]);

  return (
    <BranchContext.Provider value={{ selectedBranch, setSelectedBranch, availableBranches }}>
      {children}
    </BranchContext.Provider>
  );
};

export const useBranch = () => {
  const context = useContext(BranchContext);
  if (context === undefined) {
    throw new Error('useBranch must be used within a BranchProvider');
  }
  return context;
};

